import { atom, appJotaiStore } from "../app-jotai";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

// Active cloud drawing tracking
export const sActiveDrawingIdAtom = atom<string | null>(null);
export const sActiveDrawingNameAtom = atom<string | null>(null);

// Active folder tracking: null = root/unfiled, UUID = inside that folder
export const sActiveFolderIdAtom = atom<string | null>(null);

// Dialog / browser state
export const bCloudSaveDialogOpenAtom = atom(false);
export const bFileBrowserOpenAtom = atom(false);
export const bSaveInProgressAtom = atom(false);

// Excalidraw API reference (set via onMount)
export const excalidrawAPIAtom = atom<ExcalidrawImperativeAPI | null>(null);

/**
 * Triggers a cloud save. If active drawing exists, silently upserts.
 * Otherwise opens naming dialog.
 *
 * Called by both CloudToolbar and AppMainMenu — same logic path.
 */
export const fnTriggerCloudSave = async (
  oExcalidrawAPI: ExcalidrawImperativeAPI,
  fnGetToken: () => Promise<string | null>,
): Promise<{ bSuccess: boolean; sDrawingId?: string }> => {
  const { fnSaveDrawing } = await import("./cloudStorage");

  // Mutex check
  const bSaving = appJotaiStore.get(bSaveInProgressAtom);
  if (bSaving) {
    return { bSuccess: false };
  }

  const sActiveId = appJotaiStore.get(sActiveDrawingIdAtom);
  const sActiveName = appJotaiStore.get(sActiveDrawingNameAtom);

  if (sActiveId && sActiveName) {
    // Silent upsert — existing drawing
    appJotaiStore.set(bSaveInProgressAtom, true);
    try {
      const sToken = await fnGetToken();
      if (!sToken) {
        oExcalidrawAPI.setToast({
          message: "Not signed in. Please sign in to save.",
        });
        return { bSuccess: false };
      }

      const aElements = oExcalidrawAPI.getSceneElements();
      const oAppState = oExcalidrawAPI.getAppState();
      const oFiles = oExcalidrawAPI.getFiles();

      const oResult = await fnSaveDrawing(
        aElements,
        oAppState,
        oFiles,
        sActiveName,
        sToken,
        sActiveId,
      );

      if (oResult.bSuccess) {
        oExcalidrawAPI.setToast({ message: "Saved to cloud" });
      }
      return oResult;
    } finally {
      appJotaiStore.set(bSaveInProgressAtom, false);
    }
  }

  // No active drawing — open naming dialog
  appJotaiStore.set(bCloudSaveDialogOpenAtom, true);
  return { bSuccess: false };
};
